import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, Loader2, CheckCircle2, Send } from "lucide-react";
import { TESTIMONIALS, CONTACT } from "../data";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { CTABand } from "../components/CTABand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Testimonials() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [quote, setQuote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => setReviews(res.data || []))
      .catch(() => {});
  }, []);

  const submitReview = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Please tap the stars to give a rating first.");
      return;
    }
    if (!name.trim() || !quote.trim()) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        name: name.trim(),
        location: location.trim() || null,
        rating,
        quote: quote.trim(),
      });
      setDone(true);
      toast.success("Thank you! Your review has been sent for a quick review.");
    } catch {
      toast.error("Something went wrong. Please call us at " + CONTACT.phone);
    } finally {
      setSubmitting(false);
    }
  };

  const combined = [
    ...reviews.map((r) => ({
      name: r.name,
      role: r.location || "Verified customer",
      quote: r.quote,
      rating: r.rating,
    })),
    ...TESTIMONIALS,
  ];

  return (
    <div data-testid="testimonials-page">
      <PageHero
        eyebrow="Testimonials"
        title="Kind words from our customers"
        subtitle="We're proud of the trust our neighbors place in us. Here's what a few of them had to say."
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {combined.map((t, i) => (
            <Reveal key={`${t.name}-${i}`} delay={(i % 3) * 0.08}>
              <div
                data-testid={`testimonial-${i}`}
                className="h-full rounded-2xl border border-border bg-card p-8 flex flex-col hover:-translate-y-1 hover:shadow-xl transition-transform"
              >
                <Quote className="h-9 w-9 text-accent/30 mb-4" />
                <div className="flex text-accent mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="h-5 w-5 fill-current" />
                  ))}
                </div>
                <p className="text-slate-300 text-lg leading-relaxed flex-1">“{t.quote}”</p>
                <div className="mt-6 pt-6 border-t border-border">
                  <p className="font-bold text-foreground">{t.name}</p>
                  <p className="text-sm text-muted-foreground">{t.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Leave a review */}
      <section className="bg-secondary border-y border-border">
        <div
          data-testid="review-form-section"
          className="max-w-2xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center"
        >
          <Reveal>
            {done ? (
              <div data-testid="review-thankyou">
                <CheckCircle2 className="h-16 w-16 text-accent mx-auto mb-6" />
                <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl tracking-tight">
                  Thank you so much!
                </h2>
                <p className="mt-4 text-muted-foreground text-lg leading-relaxed">
                  We really appreciate you taking the time. Your review will appear on this
                  page shortly, once we've had a quick look.
                </p>
              </div>
            ) : (
              <>
                <h2 className="font-heading font-extrabold text-foreground text-3xl sm:text-4xl tracking-tight">
                  Share your experience
                </h2>
                <p className="mt-4 text-muted-foreground text-lg">
                  Tap the stars and leave a few words — your feedback helps other families
                  find friendly help.
                </p>

                <div
                  className="mt-8 flex justify-center gap-2"
                  onMouseLeave={() => setHover(0)}
                >
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`rating-star-${n}`}
                      onMouseEnter={() => setHover(n)}
                      onFocus={() => setHover(n)}
                      onClick={() => setRating(n)}
                      aria-label={`Rate ${n} out of 5 stars`}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star
                        className={`h-12 w-12 sm:h-14 sm:w-14 transition-colors ${
                          n <= (hover || rating)
                            ? "text-accent fill-current"
                            : "text-border"
                        }`}
                      />
                    </button>
                  ))}
                </div>

                <form onSubmit={submitReview} data-testid="review-form" className="mt-8 space-y-4 text-left">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input
                      data-testid="review-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name *"
                      className="review-input"
                    />
                    <input
                      data-testid="review-location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="City / neighborhood (optional)"
                      className="review-input"
                    />
                  </div>
                  <textarea
                    data-testid="review-quote"
                    value={quote}
                    onChange={(e) => setQuote(e.target.value)}
                    rows={4}
                    placeholder="Tell us how we did… *"
                    className="review-input resize-none h-auto py-3"
                  />
                  <button
                    type="submit"
                    data-testid="review-submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 h-14 rounded-full bg-accent text-white font-bold text-lg hover:bg-[hsl(196,82%,27%)] transition-colors disabled:opacity-60"
                  >
                    {submitting ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Sending…</>
                    ) : (
                      <><Send className="h-5 w-5" /> Submit Review</>
                    )}
                  </button>
                  <p className="text-sm text-muted-foreground text-center">
                    Reviews are checked before they appear on our site.
                  </p>
                </form>
              </>
            )}
          </Reveal>
        </div>
      </section>

      <CTABand />

      <style>{`
        .review-input {
          width: 100%;
          height: 3.5rem;
          border-radius: 0.9rem;
          border: 1px solid hsl(var(--border));
          background: hsl(var(--card));
          padding: 0 1rem;
          font-size: 1.0625rem;
          color: hsl(var(--foreground));
          outline: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .review-input:focus {
          border-color: hsl(var(--accent));
          box-shadow: 0 0 0 3px hsl(var(--accent) / 0.15);
        }
      `}</style>
    </div>
  );
}
